import { motion } from "framer-motion";
import { Home, Building2, TreePine, LandPlot, X } from "lucide-react";
import { Slider } from "@/components/ui/slider";

const ease = [0.22, 1, 0.36, 1] as const;

export type PropertyTypeFilter = "all" | "dům" | "byt" | "chata" | "pozemek";

const typeOptions: { value: PropertyTypeFilter; label: string; icon: React.ElementType; matchTypes: string[] }[] = [
  { value: "all", label: "Vše", icon: Home, matchTypes: [] },
  { value: "dům", label: "Dům", icon: Home, matchTypes: ["Dům", "Vila"] },
  { value: "byt", label: "Byt", icon: Building2, matchTypes: ["Byt", "Mezonet"] },
  { value: "chata", label: "Chata", icon: TreePine, matchTypes: ["Chata"] },
  { value: "pozemek", label: "Pozemek", icon: LandPlot, matchTypes: ["Pozemek"] },
];

const MAX_PRICE = 60_000_000;

export function parsePrice(priceStr: string): number | null {
  if (priceStr.includes("vyžádání")) return null;
  if (priceStr.includes("€")) return null;
  if (priceStr.includes("/m²") || priceStr.includes("/m\u00B2")) return null;
  const num = parseInt(priceStr.replace(/\s/g, "").replace(/[^\d]/g, ""), 10);
  return isNaN(num) ? null : num;
}

function formatPrice(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)} mil.`;
  return `${(value / 1000).toFixed(0)} tis.`;
}

interface Props {
  typeFilter: PropertyTypeFilter;
  onTypeChange: (t: PropertyTypeFilter) => void;
  priceRange: [number, number];
  onPriceChange: (r: [number, number]) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
}

const PropertyFilters = ({ typeFilter, onTypeChange, priceRange, onPriceChange, onReset, hasActiveFilters }: Props) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease }}
      className="mb-10"
    >
      <div className="glass-card p-5 sm:p-6 rounded-2xl space-y-6">
        {/* Type filter chips */}
        <div className="flex flex-wrap gap-2">
          {typeOptions.map((opt) => {
            const Icon = opt.icon;
            const active = typeFilter === opt.value;
            return (
              <motion.button
                key={opt.value}
                onClick={() => onTypeChange(opt.value)}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 border ${
                  active
                    ? "bg-primary text-primary-foreground border-primary shadow-[0_4px_20px_hsl(var(--primary)/0.25)]"
                    : "bg-secondary/50 text-muted-foreground border-border hover:bg-secondary hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4" />
                {opt.label}
              </motion.button>
            );
          })}
        </div>

        {/* Price slider */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">Cenové rozpětí</span>
            <span className="text-sm text-muted-foreground tabular-nums">
              {formatPrice(priceRange[0])} – {priceRange[1] >= MAX_PRICE ? "60+ mil. Kč" : `${formatPrice(priceRange[1])} Kč`}
            </span>
          </div>
          <Slider
            min={0}
            max={MAX_PRICE}
            step={500_000}
            value={priceRange}
            onValueChange={(v) => onPriceChange(v as [number, number])}
            className="py-1"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0 Kč</span>
            <span>60+ mil. Kč</span>
          </div>
        </div>

        {/* Reset */}
        {hasActiveFilters && (
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={onReset}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
            Zrušit filtry
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

export { typeOptions, MAX_PRICE };
export default PropertyFilters;
